import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import {
  SignOption,
  useSearchSignOptions,
  useSignOptionsByIds,
} from "../../api/signs";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";

/**
 * The two non-id states a centre picker can express, beyond "this centre".
 *
 * Only the filter uses them. The assignment form does not: "assigned to some
 * centre, I don't care which" is not something you can save on a pub.
 */
export type CentreScopeValue = "general" | "assigned";

export type CentreValue = number | CentreScopeValue | null;

export type CentreAutocompleteOption =
  | { kind: "scope"; value: CentreScopeValue; label: string }
  | { kind: "centre"; value: number; label: string; centreCode: string | null };

export interface CentreAutocompleteProps {
  value: CentreValue;
  onChange: (value: CentreValue) => void;
  label: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  /**
   * Pseudo-options shown above the real centres ("General - no centre",
   * "Assigned to a centre (any)"). Empty for the assignment form.
   */
  scopeOptions?: Array<{ value: CentreScopeValue; label: string }>;
  size?: "small" | "medium";
  className?: string;
  fullWidth?: boolean;
  disabled?: boolean;
}

const centreLabel = (sign: SignOption): string => {
  const name = sign.name ?? `#${sign.id}`;
  return sign.centre_code ? `${name} \u2014 ${sign.centre_code}` : name;
};

const toCentreOption = (sign: SignOption): CentreAutocompleteOption => ({
  kind: "centre",
  value: sign.id,
  label: centreLabel(sign),
  centreCode: sign.centre_code ?? null,
});

/**
 * SERVER-SIDE searchable centre picker.
 *
 * WHY THIS REPLACED A PLAIN <Select> AND A CLIENT-FILTERED <Autocomplete>
 * ----------------------------------------------------------------------
 * Both previous versions were built on the same assumption: that the whole
 * centre catalogue could be held in the browser. On a real deployment that is
 * tens of thousands of rows and roughly 340 kB of JSON, fetched on every visit
 * to the pub screen. Then:
 *
 *   - "Rattacher a un centre" was a <Select> rendering ONE <MenuItem> PER
 *     CENTRE. MUI mounts every child of an open Select, so opening the dropdown
 *     built thousands of DOM nodes synchronously on the main thread. That is the
 *     multi-second freeze - the network was never the bottleneck, the render was.
 *   - The filter was an <Autocomplete> with a custom `filterOptions`, so every
 *     keystroke ran a full scan of that array in JS, on the same thread that had
 *     to repaint the input.
 *
 * A picker does not need the catalogue, it needs the handful of rows matching
 * what was typed. So the search moved to the server (GET /sign/options?search=),
 * the request is debounced, and the list is bounded. Opening the dropdown is now
 * one small request and ~50 nodes regardless of how many centres exist.
 *
 * THE PRE-SELECTED VALUE PROBLEM
 * ------------------------------
 * With a bounded list, `value` is very often NOT in `options` - a pub attached to
 * centre #9312 will not appear in the first fifty alphabetically. MUI renders a
 * blank input in that case (and warns). Blank does not read as "loading", it
 * reads as "not attached", and the natural reaction is to pick something else -
 * a silent reassignment caused purely by a rendering gap.
 *
 * `useSignOptionsByIds` therefore resolves the selected id separately and the
 * result is merged into `options` below. The selected centre is always present,
 * whatever the current search term is.
 */
export const CentreAutocomplete: React.FC<CentreAutocompleteProps> = ({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  scopeOptions = [],
  size = "medium",
  className,
  fullWidth = true,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  /*
   * TWO states, not one, and the distinction is load-bearing.
   *
   * `inputValue` is what the text field DISPLAYS. `searchTerm` is what gets
   * queried. They diverge on MUI's `reason: "reset"`, which fires when the
   * component writes a selected option's label back into the field.
   *
   * Collapsing them either way produces a real bug:
   *   - one state updated on every reason -> selecting "Feu Vert Segny" issues a
   *     search FOR "Feu Vert Segny", a request whose only possible answer is the
   *     row the operator just picked;
   *   - one state ignoring "reset" -> the field is controlled but never accepts
   *     the label, so after picking an option the box still shows the half-typed
   *     text and the selection looks like it did not take.
   */
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  /*
   * `enabled: open` - a closed picker fetches NOTHING.
   *
   * The old code fetched every centre at page load whether or not anyone ever
   * touched the dropdown, which is most of the time: an operator visiting the
   * pub list to read it paid the entire cost for a control they never opened.
   */
  const { signOptions, isFetching, hasMore } = useSearchSignOptions(
    debouncedSearch,
    { enabled: open }
  );

  const selectedId = typeof value === "number" ? value : null;
  const { signOptions: selectedSigns } = useSignOptionsByIds(
    selectedId === null ? [] : [selectedId]
  );

  const scope = useMemo<CentreAutocompleteOption[]>(
    () => scopeOptions.map((option) => ({ kind: "scope", ...option })),
    [scopeOptions]
  );

  /**
   * The option list AND the option matching `value`, built together.
   *
   * Together, not in two passes, because MUI requires the selected option to be
   * REFERENTIALLY findable in `options`: a `value` that no option matches makes
   * it log "none of the options match" and render the field blank. Deriving the
   * selection separately and hoping the list contains it is exactly how that
   * warning gets shipped.
   *
   * Three sources feed the list, in this order of trust:
   *   1. the scope pseudo-options (local, always present),
   *   2. the resolved label for the current selection (useSignOptionsByIds),
   *   3. the current search page.
   *
   * If none of them can label the selection yet - the window between opening an
   * edit form and the label request resolving - a placeholder option is
   * synthesised from the id. Showing "#9312" briefly is honest; showing an empty
   * "Rattacher a un centre" field on a pub that IS attached is not, and invites
   * the operator to overwrite it.
   */
  const { options, selectedOption } = useMemo(() => {
    const centres = signOptions.map(toCentreOption);
    const seen = new Set(centres.map((option) => option.value));

    // Deduplicated by id: when the search page DOES contain the selection, it
    // must not be listed twice.
    selectedSigns.forEach((sign) => {
      if (!seen.has(sign.id)) {
        centres.unshift(toCentreOption(sign));
        seen.add(sign.id);
      }
    });

    const all: CentreAutocompleteOption[] = [...scope, ...centres];

    if (value === null) {
      return { options: all, selectedOption: null };
    }

    const match =
      all.find(
        (option) =>
          option.value === value &&
          (typeof value === "number"
            ? option.kind === "centre"
            : option.kind === "scope")
      ) ?? null;

    if (match) {
      return { options: all, selectedOption: match };
    }

    const placeholder: CentreAutocompleteOption =
      typeof value === "number"
        ? { kind: "centre", value, label: `#${value}`, centreCode: null }
        : { kind: "scope", value, label: value };

    // Prepended so it is genuinely part of `options` - that is what keeps MUI
    // from treating the value as unmatched.
    return { options: [placeholder, ...all], selectedOption: placeholder };
  }, [signOptions, selectedSigns, scope, value]);

  return (
    <Autocomplete<CentreAutocompleteOption, false, false, false>
      className={className}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={selectedOption}
      onChange={(_, option) => onChange(option ? option.value : null)}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        // Display follows every reason, so the field always shows what MUI
        // expects it to show.
        setInputValue(next);

        // The QUERY only follows real typing and the clear button - see the note
        // on the two states above.
        if (reason === "input") setSearchTerm(next);
        if (reason === "clear") setSearchTerm("");
      }}
      /*
       * The server already filtered. Re-filtering here would hide legitimate
       * matches: the API also searches centre_code and ranks prefix hits first,
       * and MUI's default filter only looks at the label string.
       *
       * Scope pseudo-options are local, so they are the one thing filtered
       * client-side - otherwise typing anything at all would leave "General" and
       * "Assigned" sitting at the top of a list of unrelated results.
       */
      filterOptions={(all, { inputValue: term }) => {
        const needle = term.trim().toLowerCase();
        if (!needle) return all;
        return all.filter(
          (option) =>
            option.kind === "centre" ||
            option.label.toLowerCase().includes(needle)
        );
      }}
      groupBy={(option) =>
        option.kind === "centre"
          ? t("pub.centresGroup", "Centres")
          : t("pub.scopeGroup", "Scope")
      }
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(a, b) => a.kind === b.kind && a.value === b.value}
      /*
       * The listbox is portalled into a Popper, so it is NOT a descendant of the
       * root element - capping its height through `sx` on the Autocomplete does
       * nothing. ListboxProps is the only handle that reaches it.
       */
      ListboxProps={{ style: { maxHeight: 320 } }}
      loading={isFetching}
      loadingText={t("common.loading", "Chargement\u2026")}
      noOptionsText={
        debouncedSearch.trim()
          ? t("pub.noCentreMatch", "Aucun centre ne correspond")
          : t("pub.typeToSearchCentre", "Tapez un nom ou un code centre")
      }
      renderOption={(props, option) => (
        <li {...props} key={`${option.kind}-${option.value}`}>
          <Box>
            <Typography variant="body2">{option.label}</Typography>
            {option.kind === "centre" && option.centreCode && (
              <Typography variant="caption" color="text.secondary">
                {option.centreCode}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={
            placeholder ?? t("pub.centrePlaceholder", "Nom ou code centre")
          }
          helperText={
            /*
             * Say so when the list is truncated.
             *
             * The bug this replaces was invisible precisely because nothing said
             * the data was incomplete - the operator saw a full-looking dropdown
             * that happened to be missing the centre they wanted. A truncated
             * list that announces itself is a different situation entirely.
             */
            hasMore
              ? t(
                  "pub.centreRefineSearch",
                  "Beaucoup de r\u00e9sultats \u2014 pr\u00e9cisez le nom ou le code centre."
                )
              : helperText
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
